"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const STOP_WORDS = new Set([
  "a",
  "about",
  "above",
  "after",
  "again",
  "all",
  "am",
  "an",
  "and",
  "any",
  "are",
  "as",
  "at",
  "be",
  "because",
  "been",
  "before",
  "being",
  "below",
  "between",
  "both",
  "but",
  "by",
  "can",
  "did",
  "do",
  "does",
  "doing",
  "down",
  "during",
  "each",
  "few",
  "for",
  "from",
  "further",
  "had",
  "has",
  "have",
  "having",
  "he",
  "her",
  "here",
  "hers",
  "him",
  "his",
  "how",
  "i",
  "if",
  "in",
  "into",
  "is",
  "it",
  "its",
  "just",
  "me",
  "more",
  "most",
  "my",
  "no",
  "nor",
  "not",
  "now",
  "of",
  "off",
  "on",
  "once",
  "only",
  "or",
  "other",
  "our",
  "out",
  "over",
  "own",
  "so",
  "some",
  "such",
  "than",
  "that",
  "the",
  "their",
  "them",
  "then",
  "there",
  "these",
  "they",
  "this",
  "those",
  "through",
  "to",
  "too",
  "under",
  "until",
  "up",
  "very",
  "was",
  "we",
  "were",
  "what",
  "when",
  "where",
  "which",
  "while",
  "who",
  "why",
  "will",
  "with",
  "you",
  "your",
]);

const PHRASE_SPLIT_REGEX = /[\n,.;:!?()"]+/;

/*
 * Hashtag words by Unicode property, not by `a-z0-9`.
 *
 * Hashtags are one of the least ASCII things on the internet, and this matched none of
 * them. `Generate Hashtags` produced an EMPTY list — no chips, no stats, no platform
 * guidance — for Russian, Arabic, Hindi, Korean, Chinese and Japanese input, because
 * `[a-z0-9]+` found no word to build a tag from. Latin with diacritics was worse than
 * empty: it silently generated tags from the fragments between the accents, so
 * "Grüße aus Köln" produced `#Gr #E #Aus #K #Ln` and "Đây là tiêu đề" produced
 * `#YLTiU #Y #L #Ti #U` — publishable-looking nonsense.
 *
 * `\p{M}` is in the class because Devanagari writes its vowels as combining marks:
 * without it "सुबह की कॉफ़ी" fragments at every matra instead of yielding three tags.
 * The `i` flag is gone because `\p{L}` already covers both cases; `u` is what makes the
 * property escapes legal.
 */
const WORD_MATCH_REGEX = /[\p{L}\p{N}\p{M}]+/gu;

/** Instagram caps a reel or post at five hashtags: @creators on Threads, 18 Dec 2025. */
export const INSTAGRAM_HASHTAG_LIMIT = 5;

type HashtagStyle = "camel" | "lower";

interface Platform {
  name: string;
  guidance: string;
  max: number;
}

function extractPhraseWords(phrase: string): string[] {
  const matches = phrase.match(WORD_MATCH_REGEX) ?? [];
  return matches.map((w) => w.toLowerCase()).filter((w) => !STOP_WORDS.has(w));
}

function extractTags(text: string): string[][] {
  const tags: string[][] = [];
  const seen = new Set<string>();
  const addTag = (words: string[]) => {
    const key = words.join("");
    if (key.length === 0 || seen.has(key)) {
      return;
    }
    seen.add(key);
    tags.push(words);
  };
  for (const phrase of text.split(PHRASE_SPLIT_REGEX)) {
    const words = extractPhraseWords(phrase);
    if (words.length >= 2 && words.length <= 4) {
      addTag(words);
    }
    for (const word of words) {
      addTag([word]);
    }
  }
  return tags;
}

function formatHashtag(words: string[], style: HashtagStyle): string {
  if (style === "lower") {
    return `#${words.join("")}`;
  }
  const camel = words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
  return `#${camel}`;
}

function countBadgeClass(count: number, max: number): string {
  if (count === 0) {
    return "text-muted-foreground";
  }
  if (count <= max) {
    return "text-green-600 dark:text-green-400";
  }
  return "text-red-600 dark:text-red-400";
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const HASHTAG_GENERATOR_STRINGS = {
  instagram: "Instagram",
  instagramGuidance:
    "Up to 5 allowed (since December 2025); a few specific tags work best",
  xTwitter: "X (Twitter)",
  xTwitterGuidance: "1-2 hashtags is the norm",
  tiktok: "TikTok",
  tiktokGuidance: "3-6 focused hashtags",
  linkedin: "LinkedIn",
  linkedinGuidance: "3-5 professional hashtags",
  inputLabel:
    "Caption or keywords — separate phrases with commas or line breaks",
  inputPlaceholder: "morning coffee ritual\nslow living, cozy home",
  hashtagStyle: "Hashtag style",
  camelCaseLabel: "#CamelCase",
  lowercaseLabel: "#lowercase",
  generateHashtags: "Generate Hashtags",
  copied: "Copied!",
  copyAll: "Copy All",
  clear: "Clear",
  yourHashtags: "Your hashtags — click a chip to remove it",
  removeHashtagTitle: "Remove hashtag",
  addHashtagManually: "Add a hashtag manually",
  manualInputPlaceholder: "e.g. book lovers",
  add: "Add",
  platformGuidance: "Platform Guidance",
  hashtags: "Hashtags",
  characters: "Characters",
  style: "Style",
  camelCase: "CamelCase",
  lowercase: "lowercase",
} as const;

export default function HashtagGeneratorWidget() {
  const [input, setInput] = useState("");
  const [style, setStyle] = useState<HashtagStyle>("camel");
  const [tags, setTags] = useState<string[][]>([]);
  const [manual, setManual] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(HASHTAG_GENERATOR_STRINGS);

  const fmt = useToolNumber();

  const platforms: Platform[] = [
    {
      name: strings.instagram,
      guidance: strings.instagramGuidance,
      max: INSTAGRAM_HASHTAG_LIMIT,
    },
    { name: strings.xTwitter, guidance: strings.xTwitterGuidance, max: 2 },
    { name: strings.tiktok, guidance: strings.tiktokGuidance, max: 6 },
    { name: strings.linkedin, guidance: strings.linkedinGuidance, max: 5 },
  ];

  const formatted = tags.map((t) => formatHashtag(t, style));
  const allText = formatted.join(" ");

  function handleGenerate() {
    if (input.trim().length === 0) {
      return;
    }
    setTags(extractTags(input));
    setCopied(false);
  }

  function handleAddManual() {
    const words = extractPhraseWords(manual);
    if (words.length === 0) {
      return;
    }
    const key = words.join("");
    const exists = tags.some((t) => t.join("") === key);
    if (!exists) {
      setTags([...tags, words]);
    }
    setManual("");
    setCopied(false);
  }

  function handleRemove(index: number) {
    setTags(tags.filter((_, i) => i !== index));
    setCopied(false);
  }

  function handleCopy() {
    if (allText.length === 0) {
      return;
    }
    writeToClipboard(allText).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setTags([]);
    setManual("");
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="hashtag-input"
        >
          {strings.inputLabel}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="hashtag-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Style toggle */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.hashtagStyle}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={style === "camel"}
              className="accent-primary"
              name="hashtag-style"
              onChange={() => setStyle("camel")}
              type="radio"
              value="camel"
            />
            {strings.camelCaseLabel}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={style === "lower"}
              className="accent-primary"
              name="hashtag-style"
              onChange={() => setStyle("lower")}
              type="radio"
              value="lower"
            />
            {strings.lowercaseLabel}
          </label>
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateHashtags}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={tags.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyAll}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && tags.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Chips */}
      {tags.length > 0 && (
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.yourHashtags}
          </p>
          <div className="flex flex-wrap gap-2">
            {formatted.map((tag, index) => (
              <button
                className="rounded-full border border-border bg-card px-3 py-1.5 text-foreground text-sm transition-colors hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                key={tag}
                onClick={() => handleRemove(index)}
                title={strings.removeHashtagTitle}
                type="button"
              >
                {tag} ×
              </button>
            ))}
          </div>
          <textarea
            className="min-h-[100px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            readOnly
            value={allText}
          />
        </section>
      )}

      {/* Manual add */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="manual-hashtag"
        >
          {strings.addHashtagManually}
        </label>
        <div className="flex flex-wrap gap-3">
          <input
            className="min-w-0 flex-1 rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="manual-hashtag"
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddManual();
              }
            }}
            placeholder={strings.manualInputPlaceholder}
            type="text"
            value={manual}
          />
          <button
            className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={manual.trim().length === 0}
            onClick={handleAddManual}
            type="button"
          >
            {strings.add}
          </button>
        </div>
      </div>

      {/* Platform guidance */}
      {tags.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.platformGuidance}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {platforms.map((p) => (
              <div
                className="rounded-lg border border-border bg-card p-5"
                key={p.name}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <p className="font-medium text-foreground">{p.name}</p>
                  <p
                    className={`text-sm ${countBadgeClass(tags.length, p.max)}`}
                  >
                    {tags.length}/{p.max}
                  </p>
                </div>
                <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                  {p.guidance}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Stats */}
      {tags.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.hashtags} value={tags.length} />
          <StatCard label={strings.characters} value={fmt(allText.length)} />
          <StatCard
            label={strings.style}
            value={style === "camel" ? strings.camelCase : strings.lowercase}
          />
        </div>
      )}
    </>
  );
}
